import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type FormView, type FormViewInput } from '@/types/schema/views/FormView';
import { TabsUnderline } from '@/components/TabsUnderline';
import { useActiveViewContext } from '@/pages/pages/settings-panel/view-settings/ActiveViewContextProvider';
import { FormFieldManagement } from './field-management/FormFieldManagement';

interface FormFieldsTabTriggerProps {
  value: string;
  label: string;
  count: number;
}

function FormFieldsTabTrigger({ value, label, count }: FormFieldsTabTriggerProps) {
  return (
    <TabsUnderline.Trigger value={value} className="group">
      <span>{label}</span>
      <span className="ml-2 rounded-sm bg-subtle px-1.5 py-1 text-xs font-medium group-data-[state=active]:bg-brand-muted">
        <span className="group-data-[state=active]:text-brand-gradient">{count}</span>
      </span>
    </TabsUnderline.Trigger>
  );
}

export function FormFieldsCategory() {
  const [t] = useTranslation();
  const { view } = useActiveViewContext<FormView>();

  // The non-static field inputs that have been already added to the form
  const activeFieldInputs = useMemo(
    () =>
      view.groups.reduce((acc, group) => {
        group.columns.forEach((column) => {
          column.inputs.forEach((input) => {
            if (input.type !== 'divider' && input.type !== 'section_break') {
              acc.push(input);
            }
          });
        });
        return acc;
      }, [] as FormViewInput[]),
    [view.groups]
  );

  return (
    <TabsUnderline defaultValue="fields" className="flex h-full flex-col">
      <TabsUnderline.List>
        <FormFieldsTabTrigger
          value="fields"
          label={t('pages.element_settings.form.categories.form_fields.fields')}
          count={activeFieldInputs.length}
        />
        <FormFieldsTabTrigger
          value="display-rules"
          label={t('pages.element_settings.form.categories.form_fields.display_rules')}
          count={view.rules.fields?.length ?? 0}
        />
      </TabsUnderline.List>
      <TabsUnderline.Content
        value="fields"
        className="data-[state=active]:flex data-[state=active]:grow data-[state=active]:flex-col"
      >
        <FormFieldManagement />
      </TabsUnderline.Content>
      <TabsUnderline.Content
        value="display-rules"
        className="data-[state=active]:flex data-[state=active]:grow data-[state=active]:flex-col"
      >
        {/* TODO: FE-1709 */}
        <div>Display Rules</div>
      </TabsUnderline.Content>
    </TabsUnderline>
  );
}
